import React, { useState, useRef } from 'react';
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/authService';
import FormErrorWrapper from '../common/FormErrorWrapper';

// Format phone number to (###) ###-####
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return 'Not available';

  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Check if the number has exactly 10 digits
  if (cleaned.length !== 10) return phoneNumber;

  // Format as (###) ###-####
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

const AccountPage = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    address1: user?.address1 || '',
    city: user?.city || '',
    state: user?.state || '',
    zip_code: user?.zip_code || '',
    birth_month: user?.birth_month || '',
    birth_day: user?.birth_day || '',
    birth_year: user?.birth_year || ''
  });
  const [birthdayError, setBirthdayError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  // Password reset modal state
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [resetError, setResetError] = useState({});
  const [codeSent, setCodeSent] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const inputRefs = useRef([]);

  // Get days in month helper function
  const getDaysInMonth = (month, year) => {
    // If month is February and it's a leap year
    if (month === '2') {
      if (year) {
        const isLeapYear = (parseInt(year) % 4 === 0 && parseInt(year) % 100 !== 0) || parseInt(year) % 400 === 0;
        return isLeapYear ? 29 : 28;
      }
      return 29; // Default to max possible days if no year
    }

    // Months with 31 days
    if (['1', '3', '5', '7', '8', '10', '12'].includes(month)) {
      return 31;
    }

    // Months with 30 days
    if (['4', '6', '9', '11'].includes(month)) {
      return 30;
    }

    return 31; // Default for empty month
  };

  // Validate birthday fields
  const validateBirthday = (data) => {
    const { birth_month, birth_day, birth_year } = data;

    // If both month and day are empty, that's valid
    if (!birth_month && !birth_day) {
      setBirthdayError('');
      return true;
    }

    // If one is set but the other isn't, that's invalid
    if ((birth_month && !birth_day) || (!birth_month && birth_day)) {
      setBirthdayError('Both month and day must be selected together');
      return false;
    }

    // Check if day is valid for selected month
    const daysInMonth = getDaysInMonth(birth_month, birth_year);
    if (parseInt(birth_day) > daysInMonth || parseInt(birth_day) < 1) {
      setBirthdayError(`Invalid day for selected month. Day should be between 1 and ${daysInMonth}`);
      return false;
    }

    // All validations passed
    setBirthdayError('');
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  // Password reset handlers
  const handleOpenResetModal = () => {
    setResetModalOpen(true);
    setResetError({});
    setCodeSent(false);
    setResetSuccess(false);
    setVerificationCode(['', '', '', '', '', '']);
    setNewPassword('');
    setConfirmNewPassword('');
  };

  const handleCloseResetModal = () => {
    if (!isResetting && !isSendingCode) {
      setResetModalOpen(false);
    }
  };

  const handleSendCode = async () => {
    setResetError({});
    setIsSendingCode(true);

    try {
      await authService.requestPasswordReset(user.email);
      setCodeSent(true);
      // Focus on the first input once code is sent
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }, 100);
    } catch (err) {
      console.error('Error sending reset code:', err);
      setResetError({ general: 'Failed to send verification code. Please try again.' });
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newCode = [...verificationCode];
      newCode[index] = value;
      setVerificationCode(newCode);
      
      // Auto-focus next input
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...verificationCode];
    
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newCode[i] = pastedData[i];
    }
    
    setVerificationCode(newCode);
    
    // Focus on the last filled input or the next empty one
    const lastFilledIndex = Math.min(pastedData.length - 1, 5);
    inputRefs.current[lastFilledIndex]?.focus();
  };

  const handleResetPassword = async () => {
    setResetError({});
    
    // Validation
    if (verificationCode.some(digit => digit === '')) {
      setResetError({ code: 'Please enter the complete verification code' });
      return;
    }
    
    if (!newPassword) {
      setResetError({ password: 'Password is required' });
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      setResetError({ confirm_password: 'Passwords do not match' });
      return;
    }
    
    setIsResetting(true);

    try {
      const code = verificationCode.join('');
      await authService.resetPassword({
        email: user.email,
        code,
        password: newPassword,
        confirm_password: confirmNewPassword
      });

      setResetSuccess(true);
      setTimeout(() => {
        setResetModalOpen(false);
        // Clear the form
        setVerificationCode(['', '', '', '', '', '']);
        setNewPassword('');
        setConfirmNewPassword('');
        setCodeSent(false);
        setResetSuccess(false);
      }, 2000);
    } catch (err) {
      const errorData = err.response?.data || {};
      setResetError(errorData);
      
      // Clear verification code on error
      if (errorData.code) {
        setVerificationCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } finally {
      setIsResetting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(false);
    setError('');
    setBirthdayError(''); // Clear any previous birthday errors

    // Validate birthday fields before submission
    if (!validateBirthday(formData)) {
      return; // Don't proceed if validation fails
    }

    setIsSubmitting(true);

    try {
      // Convert empty strings to null before submission
      const processedData = Object.entries(formData).reduce((acc, [key, value]) => {
        acc[key] = value === '' ? null : value;
        return acc;
      }, {});

      // Use authService to update profile in the database
      const response = await authService.updateProfile(processedData);

      // Update the user in auth context with the new data
      updateUser(processedData);

      setSuccess(true);
    } catch (err) {
      setError('Failed to update account information. Please try again.');
      console.error('Update profile error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ px: 4, py: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56, mr: 2 }}>
            <AccountCircleIcon fontSize="large" />
          </Avatar>
          <Typography variant="h4" component="h1">
            Account Settings
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Your account information has been updated successfully.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item size={6}>
              <TextField
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item size={6}>
              <TextField
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item size={12}>
              <TextField
                label="Address"
                name="address1"
                value={formData.address1}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item size={4}>
              <TextField
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item size={4}>
              <TextField
                label="State"
                name="state"
                value={formData.state}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item size={4}>
              <TextField
                label="ZIP Code"
                name="zip_code"
                value={formData.zip_code}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item size={3}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                Birthday Information
              </Typography>
            </Grid>
            <Grid item size={3}>
              <TextField
                select
                label="Month"
                name="birth_month"
                value={formData.birth_month}
                onChange={handleChange}
                fullWidth
                disabled={user?.birth_month}
                SelectProps={{
                  native: true,
                }}
              >
                <option value=""></option>
                <option value="1">January</option>
                <option value="2">February</option>
                <option value="3">March</option>
                <option value="4">April</option>
                <option value="5">May</option>
                <option value="6">June</option>
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </TextField>
            </Grid>
            <Grid item size={3}>
              <TextField
                select
                label="Day"
                name="birth_day"
                disabled={user?.birth_day}
                value={formData.birth_day}
                onChange={handleChange}
                fullWidth
                SelectProps={{
                  native: true,
                }}
              >
                <option value=""></option>
                {[...Array(31)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item size={3}>
              <TextField
                label="Year (Optional)"
                name="birth_year"
                value={formData.birth_year}
                onChange={handleChange}
                fullWidth
                inputProps={{
                  maxLength: 4,
                  pattern: '[0-9]*'
                }}
                placeholder="YYYY"
              />
            </Grid>
            {birthdayError && (
              <Grid item size={12}>
                <Alert severity="error" sx={{ mt: 1 }}>
                  {birthdayError}
                </Alert>
              </Grid>
            )}
            <Grid item size={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item size={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Contact Information (cannot be changed)
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Email: {user?.email || 'Not available'}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Phone: {formatPhoneNumber(user?.phone)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  To change your email or phone number, please contact customer support.
                </Typography>
              </Box>
            </Grid>
            <Grid item size={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleOpenResetModal}
                  startIcon={<LockResetIcon />}
                >
                  Reset Password
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Password Reset Modal */}
      <Dialog
        open={resetModalOpen}
        onClose={handleCloseResetModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Reset Password
        </DialogTitle>
        <DialogContent>
          {resetSuccess ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h6" color="success.main" gutterBottom>
                Password Reset Successful!
              </Typography>
              <Typography variant="body1">
                Your password has been updated successfully.
              </Typography>
            </Box>
          ) : !codeSent ? (
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" gutterBottom>
                We'll send a verification code to your email address:
              </Typography>
              <Typography variant="body2" color="primary" fontWeight="bold" sx={{ mb: 3 }}>
                {user?.email}
              </Typography>
              {resetError.general && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {resetError.general}
                </Alert>
              )}
              <Button
                variant="contained"
                fullWidth
                onClick={handleSendCode}
                disabled={isSendingCode}
                startIcon={isSendingCode ? <CircularProgress size={20} /> : null}
              >
                {isSendingCode ? 'Sending...' : 'Send Verification Code'}
              </Button>
            </Box>
          ) : (
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" gutterBottom>
                Enter the 6-digit verification code sent to {user?.email}
              </Typography>
              
              <Stack direction="row" spacing={1} justifyContent="center" sx={{ my: 3 }}>
                {verificationCode.map((digit, index) => (
                  <TextField
                    key={index}
                    inputRef={el => inputRefs.current[index] = el}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    onPaste={index === 0 ? handleCodePaste : undefined}
                    variant="outlined"
                    inputProps={{
                      maxLength: 1,
                      style: { textAlign: 'center', fontSize: '1.5rem' },
                      inputMode: 'numeric',
                      pattern: '[0-9]*'
                    }}
                    sx={{ width: 50 }}
                    disabled={isResetting}
                  />
                ))}
              </Stack>
              
              {resetError.code && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {resetError.code}
                </Alert>
              )}
              
              {resetError.non_field_errors && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {resetError.non_field_errors}
                </Alert>
              )}
              
              <FormErrorWrapper error={resetError.password}>
                <TextField
                  fullWidth
                  type="password"
                  label="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  sx={{ mb: 2 }}
                  disabled={isResetting}
                />
              </FormErrorWrapper>
              
              <FormErrorWrapper error={resetError.confirm_password}>
                <TextField
                  fullWidth
                  type="password"
                  label="Confirm New Password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  sx={{ mb: 2 }}
                  disabled={isResetting}
                />
              </FormErrorWrapper>
              
              <Button
                variant="contained"
                fullWidth
                onClick={handleResetPassword}
                disabled={isResetting}
                startIcon={isResetting ? <CircularProgress size={20} /> : null}
              >
                {isResetting ? 'Resetting...' : 'Reset Password'}
              </Button>
            </Box>
          )}
        </DialogContent>
        {!resetSuccess && (
          <DialogActions>
            <Button onClick={handleCloseResetModal} disabled={isResetting || isSendingCode}>
              Cancel
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Container>
  );
};

export default AccountPage;